import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getExchangeCollectiveData,
  getExchangeCollectiveForecastData,
} from "../../../Rest_api/RestApi";
import { ExchangeColors, ExchangeData } from "../Exchange3/FormatData";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { renderHourTick } from "../../../components/recharts/components";
import { ChartAreaLoader } from "../../../components/Loading";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import FilterMenu from "../../../components/FilterMenu";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { ExchangeDataTabs } from "../../Rajasthan/Demand/Data";
import { formatDateToLocal } from "./PriceForcastingIEXDAM";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

const chartSuffixes = [
  {
    suffix: "wt_mcp_rs_kwh",
    displayName: "",
    stackSuffix: "wt_mcp_rs_kwh",
  },
];

const Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const H_CHART_TYPES = [
  { value: ChartType.Bar, label: "Standard Bar", stackId: undefined },
  { value: ChartType.Bar, label: "Stacked Bar", stackId: "a" },
  { value: ChartType.Line, label: "Line" },
];

export function getLastWeekDateRange() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  return {
    startDate: sevenDaysAgo,
    endDate: today,
    key: "selection",
  };
}

const PriceForcastingRTM = () => {
  const [data, setData] = useState<any[]>([]);
  const [selectedSuffix, setSelectedSuffix] = useState([chartSuffixes[0]]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[2]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedDate, setSelectedDate] = useState<{ value: string }[]>([]);
  const [selectedslot, setSelectedslot] = useState<any[]>([]);
  const [uniqueDate, setUniqueDate] = useState([]);
  const [uniqueslot, setUniqueslot] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);

  // Read from Redux store
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);

  const dates = DefaultDates?.[activeTab]?.defaultRangeDate;

  const _handlefetchData = useCallback(async () => {
    if (!dates) return;
    setLoading(true);

    const startDateStr = formatDateToLocal(new Date(dates.startDate));
    const endDateStr = formatDateToLocal(new Date(dates.endDate));

    const newData = await getExchangeCollectiveData(startDateStr, endDateStr);
    const forecastData = await getExchangeCollectiveForecastData(
      startDateStr,
      endDateStr,
      "RTM",
      "IEX"
    );

    setData([...forecastData, ...newData]);
    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item: ExchangeData) => item.date)
            .sort(
              (a: string, b: string) =>
                new Date(a).getTime() - new Date(b).getTime()
            )
        )
      )
    );
    setUniqueslot(
      Array.from(new Set(newData.map((item: ExchangeData) => item.slot)))
    );
    setLoading(false);
  }, [dates]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    let filteredData = data;

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((item) => item.value).includes(item.date)
      );
    }
    if (selectedslot.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedslot.map((item) => item.value).includes(item.slot)
      );
    }
    filteredData = filteredData.filter(
      (item) => item.exchange__name === "IEX" && item.product__name === "RTM"
    );

    const par: ChartDataParams = {
      keys: selectedInterval.values,
      tableKeys: ["date", "slot"],
      groupBy: ["type"],
      volumeKey: ``,
      aggregationKeys: ["wt_mcp_rs_kwh", "mcv_mw"],
      priceKey: "",
      data: filteredData,
      reverseSort: ["slot", "date"],
    };
    const cData = FormatChartData(par);
    const sortedData = [...cData.data].sort((a, b) => {
      const dateA = new Date(a.date || a.month || a.year || 0);
      const dateB = new Date(b.date || b.month || b.year || 0);
      return dateA.getTime() - dateB.getTime();
    });

    setChartData(sortedData);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [data, selectedDate, selectedInterval.values, selectedslot]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      return "slot";
    } else {
      return selectedInterval.key;
    }
  }, [selectedInterval.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedInterval: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[]
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`.trim(),
      legendColor: undefined,
      unit: suffix.includes("mcp") ? "Rs/kWh" : "KW",
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  function getLegends(includeActual: boolean | undefined = false) {
    return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
      chartKeys
        .map((key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedInterval,
            selectedChartType as any,
            ExchangeColors
          )
        )
        .filter((item) => !(includeActual && item.name.includes("Actual")))
    );
  }

  const tablePar = {
    legends: getLegends(true),
    data: chartData,
    tableData: tableData,
    itemsPerPage: 96,
    unit: "Rs/KWh",
    extraKeys: selectedInterval.isTime
      ? [xDataKey, selectedInterval.values[1]]
      : [selectedInterval.values[0]],
    extraLabels: selectedInterval.values,
    filename: "Price Forecasting RTM",
    showTotal: false,
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={Intervals.map((i) => i.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(Intervals[index]);
              setSelectedslot([]);
            }}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading RTM Price Forecast"
              subtitle="Fetching IEX RTM price forecast data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-60">RTM Price Forecast (Rs/KWh)</h2>
                <div className="flex gap-x-6 justify-end">
                  {selectedInterval.isTime && (
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Select Block"
                      selectedOptions={selectedslot.map((item) => Number(item.value))}
                      setSelectedOptions={setSelectedslot as any}
                    />
                  )}
                  <button
                    onClick={() => {
                      dataTab?.label === "Chart" &&
                        downloadChartTableCSV(tablePar as any);
                      dataTab?.label === "Summary" &&
                        downloadSummaryCSV(tablePar as any);
                      dataTab?.label === "Table" &&
                        downloadChartTableCSV(tablePar as any);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>
              {dataTab?.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.End}
                  isTimeSlot={selectedslot.length < 1 && selectedInterval.isTime}
                  onlyTitle={
                    !(selectedslot.length < 1 && selectedInterval.isTime)
                  }
                  barCategoryGap={10}
                  unit={"Rs/KWh"}
                  yAxisLabel={"Rs/KWh"}
                  legends={getLegends() as any}
                  xDataKey={xDataKey}
                  nameGap={40}
                  xTick={
                    selectedInterval.isTime && selectedslot.length == 0
                      ? (value: any, index: any) => {
                        return renderHourTick(value, 10);
                      }
                      : undefined
                  }
                  secondXDataKey={
                    selectedInterval.isTime && selectedslot.length == 0
                      ? selectedInterval.values[1]
                      : undefined
                  }
                />
              )}
              {dataTab?.label === "Summary" && (
                <LegendSummaryTable {...(tablePar as any)} />
              )}
              {dataTab?.label === "Table" && (
                <ChartTable {...(tablePar as any)} />
              )}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5 ">
            <p className="gna-footer-text col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
};

export default PriceForcastingRTM;
